import React, { useEffect, useMemo } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import ProfileForm from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Profile/ProfileForm';
import {
  FinalFormProfile,
  FinalFormProfileSchema
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/types/profileTypes';
import { useGetSingleProfile } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/useProfiles';
import { useProfileSummaryStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import { ProfileViewMode } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores/profileSummaryStore';
import ProfileSettingsSkeleton from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Profile/ProfileSettingsSkeleton';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { useAppSelector } from 'src/utils/Hooks';
import { getQueryParamValue } from 'src/utils/app';

const ProfileSettings = () => {
  const viewMode = useProfileSummaryStore((s) => s.viewMode);
  const editingProfileId = useProfileSummaryStore((s) => s.editingProfileId);
  const duplicateProfileData = useProfileSummaryStore((s) => s.duplicateProfileData);
  const setDuplicateProfileData = useProfileSummaryStore((s) => s.setDuplicateProfileData);
  const setViewMode = useProfileSummaryStore((s) => s.setViewMode);
  const setEditingProfileId = useProfileSummaryStore((s) => s.setEditingProfileId);
  const resetStore = useProfileSummaryStore((s) => s.reset);
  const userId = useAppSelector((state) => state.user.session?.userId);

  // Initialize viewMode and editingProfileId from URL parameters on mount
  // This ensures state is restored after page refresh
  useEffect(() => {
    const urlMode = getQueryParamValue('mode', '') as ProfileViewMode | '';
    const urlProfileId = getQueryParamValue('profileId', '');

    if (urlMode === 'add' || urlMode === 'edit') {
      setViewMode(urlMode);
      if (urlMode === 'edit' && urlProfileId) {
        setEditingProfileId(urlProfileId);
      }
    }
  }, [setViewMode, setEditingProfileId]);

  const { profile, isLoadingSingleProfile, error } = useGetSingleProfile(
    viewMode === 'edit' ? editingProfileId : undefined
  );

  // Check if current user is the owner of the profile (for edit mode)
  const isOwner = useMemo(() => {
    if (viewMode === 'add') return true; // New profiles are always editable
    if (!profile || !userId) return false;
    return String((profile as any).createdBy) === String(userId);
  }, [viewMode, profile, userId]);

  // Non-owners should view in read-only mode
  const readOnly = viewMode === 'edit' && !isOwner;

  const methods = useForm<FinalFormProfile>({
    mode: 'all',
    resolver: zodResolver(FinalFormProfileSchema)
  });

  useEffect(() => {
    if (viewMode === 'add' && duplicateProfileData) {
      const formProfile = { variant: 'add' as const, ...duplicateProfileData };
      methods.reset(formProfile);
      setDuplicateProfileData(null);
      return;
    }

    if (profile && (viewMode === 'add' || (viewMode === 'edit' && !isLoadingSingleProfile))) {
      const formProfile = viewMode === 'edit' ? { variant: 'edit' as const, ...profile } : profile;
      methods.reset(formProfile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, isLoadingSingleProfile, viewMode, methods]);

  const { beaconNavigator } = useBeaconNavigator();

  const handleBackToList = () => {
    resetStore();
    methods.reset();
    beaconNavigator.updateSearchParams({ tab: BEACON_TABS.PROFILES, subtab: BEACON_SUBTABS.PROFILES_ACTIVE });
    beaconNavigator.go();
  };

  const handleFormSuccess = () => {
    handleBackToList();
  };

  

  if (isLoadingSingleProfile && viewMode === 'edit') {
    return <ProfileSettingsSkeleton />;
  }

  if (error && viewMode === 'edit') {
    return (
      <BasePageContainer>
        <Flex>
          <div>Profile not found</div>
        </Flex>
      </BasePageContainer>
    );
  }

  return (
    <BasePageContainer>
      <Flex flexDirection="column">
        <Flex justifyContent="space-between" alignItems="center">
          <Text variant="h4">Content Profile</Text>
        </Flex>
        {['add', 'edit'].includes(viewMode) && (
          <Text variant="body1" sx={{ marginTop: '8px' }}>
            Use this page to define the parameters for this content profile. Profiles are used as a basis for
            generating new content. 
          </Text>
        )}
        <FormProvider {...methods}>
          <ProfileForm onBack={handleBackToList} onSuccess={handleFormSuccess} readOnly={readOnly} />
        </FormProvider>
      </Flex>
    </BasePageContainer>
  );
};

export default ProfileSettings;
